import {
  AddIcon,
  Button,
  Card,
  Checkbox,
  DotsHorizontalIcon,
  Dropdown,
  DropdownMenu,
  SearchInput,
  Select,
} from "@bp/ui-components";
import type { SelectOptionType } from "@bp/ui-components/dist/@bp/lib/Select/Select";
import type { MultiValue, SingleValue } from "react-select";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "urql";
import { type FragmentType, getFragmentData, graphql } from "../../../client/gql";
import { useConfirm } from "../../../hooks/useConfirm";
import { UserStatusIcon } from "../../brand/UserStatusIcon";
import { PersonLabel } from "../../PersonLabel/PersonLabel";
import { StatusBadge } from "../../StatusBadge/StatusBadge";
import {
  ACCOUNT_STATUS_BADGE,
  ACCOUNT_STATUS_ICON,
  type AccountStatus,
  accountStatus,
} from "../../../utils/accountStatus";
import { sendInvitation } from "../../../utils/invitation";
import { invitationProductUrls } from "../../../utils/product";
import { showSuccessToast, showUserErrorToast } from "../../../utils/toast";
import { MemberList, MemberRow } from "../OmniOrgView/MemberRow";
import { AddMemberModal } from "../modals/AddMemberModal";
import { BulkLicenseModal } from "../modals/BulkLicenseModal";
import { type EditableProfile, EditProfileModal } from "../modals/EditProfileModal";
import { LicenseModal } from "../modals/LicenseModal";
import { LinkAccountModal } from "../modals/LinkAccountModal";
import css from "../orgList.module.scss";

// Colocated fragments: this panel owns the org node it needs (uuid/name) plus
// the member (Profile) shape it renders.
const MembersPanel_Organization = graphql(`
  fragment MembersPanel_Organization on Organization {
    uuid
    name
    licensedApplicationsConnection {
      edges {
        node {
          clientId
          uri
        }
      }
    }
  }
`);

const MembersPanel_Profile = graphql(`
  fragment MembersPanel_Profile on Profile {
    uuid
    firstName
    lastName
    email
    user {
      uuid
      emailVerified
      invitationOtpValidUntil
      impersonateOnly
    }
    rolesConnection {
      edges {
        properties {
          names
        }
        node {
          __typename
          ... on Organization {
            uuid
          }
        }
      }
    }
  }
`);

const MembersPanelQuery = graphql(`
  query MembersPanel_Query($where: ProfileWhere) {
    profiles(where: $where, options: { sort: { lastName: ASC } }) {
      ...MembersPanel_Profile
    }
  }
`);

const MembersPanelDeleteProfile = graphql(`
  mutation MembersPanel_DeleteProfile($where: ProfileWhere) {
    deleteProfiles(where: $where) {
      __typename
    }
  }
`);

const ROLE_LABEL: Record<string, string> = {
  admin: "Admin",
  owner: "Inhaber:in",
  member: "Mitglied",
  teacher: "Lehrkraft",
  student: "Schüler:in",
  parent: "Elternteil",
  other: "Sonstige",
};
const STATUS_OPTIONS: SelectOptionType[] = [
  { value: "all", label: "Jeder Status" },
  { value: "active", label: "Aktiv" },
  { value: "invited", label: "Einladung offen" },
  { value: "unverified", label: "E-Mail unbestätigt" },
  { value: "impersonateOnly", label: "Nur über Elternaccount" },
  { value: "noAccount", label: "Kein Konto" },
];

type ProfileLike = {
  user?: {
    emailVerified?: boolean;
    invitationOtpValidUntil?: unknown;
    impersonateOnly?: boolean | null;
  } | null;
  email?: string | null;
  rolesConnection?: {
    edges?: ReadonlyArray<{
      properties?: { names?: readonly string[] | null } | null;
      node?: { __typename?: string; uuid?: string } | null;
    }>;
  } | null;
};

function rolesForOrg(profile: ProfileLike, orgUuid: string): string[] {
  const out = new Set<string>();
  for (const edge of profile.rolesConnection?.edges ?? []) {
    if (edge.node?.__typename === "Organization" && edge.node.uuid === orgUuid) {
      for (const name of edge.properties?.names ?? []) {
        out.add(ROLE_LABEL[name.toLowerCase()] ?? name);
      }
    }
  }
  return [...out];
}

/** First raw org-directed role name (TEACHER/STUDENT/…) for prefilling the edit modal. */
function rawRoleForOrg(profile: ProfileLike, orgUuid: string): string | null {
  for (const edge of profile.rolesConnection?.edges ?? []) {
    if (edge.node?.__typename === "Organization" && edge.node.uuid === orgUuid) {
      const names = edge.properties?.names ?? [];
      if (names.length > 0) {
        return names[0];
      }
    }
  }
  return null;
}

/** Presentation of the shared account status; "Aktiv" needs no badge in a member row. */
function memberPresentation(status: AccountStatus) {
  const { icon, tint } = ACCOUNT_STATUS_ICON[status];
  const badge = status === "active" ? undefined : ACCOUNT_STATUS_BADGE[status];
  return { label: ACCOUNT_STATUS_BADGE[status].label, icon, tint, badge };
}

const memberStatus = (profile: ProfileLike): AccountStatus => accountStatus(profile.user);

export function MembersPanel({
  org: orgRef,
}: {
  org: FragmentType<typeof MembersPanel_Organization>;
}) {
  const org = getFragmentData(MembersPanel_Organization, orgRef);
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [{ data }, refetch] = useQuery({
    query: MembersPanelQuery,
    variables: { where: { organization: { uuid: org.uuid } } },
  });
  const [, deleteProfile] = useMutation(MembersPanelDeleteProfile);
  const { confirm, ConfirmationDialog } = useConfirm();
  const [adding, setAdding] = useState(false);
  const [addRole, setAddRole] = useState("TEACHER");
  const [editing, setEditing] = useState<EditableProfile | null>(null);
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState<"all" | AccountStatus>("all");
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [inviteBusy, setInviteBusy] = useState(false);
  const [licenseTarget, setLicenseTarget] = useState<{ uuid: string; name: string } | null>(null);
  const [bulkLicenseOpen, setBulkLicenseOpen] = useState(false);
  const [linking, setLinking] = useState<{ uuid: string; name: string } | null>(null);

  const profiles = getFragmentData(MembersPanel_Profile, data?.profiles ?? []);
  const roleOptions = useMemo(() => {
    const set = new Set<string>();
    for (const p of profiles) {
      for (const r of rolesForOrg(p, org.uuid)) {
        set.add(r);
      }
    }
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [profiles, org.uuid]);
  const roleSelectOptions = useMemo<SelectOptionType[]>(
    () => [
      { value: "all", label: "Alle Rollen" },
      ...roleOptions.map((r) => ({ value: r, label: r })),
    ],
    [roleOptions],
  );
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return profiles.filter((p) => {
      const name = `${p.firstName ?? ""} ${p.lastName ?? ""}`.toLowerCase();
      if (s && !name.includes(s) && !(p.email ?? "").toLowerCase().includes(s)) {
        return false;
      }
      if (roleFilter !== "all" && !rolesForOrg(p, org.uuid).includes(roleFilter)) {
        return false;
      }
      if (statusFilter !== "all" && memberStatus(p) !== statusFilter) {
        return false;
      }
      return true;
    });
  }, [profiles, q, roleFilter, statusFilter, org.uuid]);

  const allSelected = filtered.length > 0 && filtered.every((p) => selected.has(p.uuid));
  const toggleAll = () =>
    setSelected(allSelected ? new Set() : new Set(filtered.map((p) => p.uuid)));

  const onDelete = async (uuid: string, name: string) => {
    const ok = await confirm({
      title: "Profil löschen",
      message: `„${name}" aus ${org.name} entfernen?`,
      confirmText: "Löschen",
      cancelText: "Abbrechen",
    });
    if (!ok) {
      return;
    }
    await deleteProfile({ where: { uuid } });
    showSuccessToast(`${name} entfernt`);
  };

  const toggleSel = (uuid: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(uuid)) {
        next.delete(uuid);
      } else {
        next.add(uuid);
      }
      return next;
    });

  // Invitations from the members list are for the Bildungsplattform; its URL
  // comes from the org's licensed application so the person lands there.
  const inviteUrls = invitationProductUrls(
    (org.licensedApplicationsConnection?.edges ?? []).map((e) => e.node),
  );

  const inviteOne = async (profileUuid: string, email: string) => {
    try {
      await sendInvitation(profileUuid, email, "bp", inviteUrls.bp);
      showSuccessToast(`Einladung an ${email} gesendet`);
    } catch (error) {
      showUserErrorToast({ text: "Einladung fehlgeschlagen", error });
    }
  };

  const bulkInvite = async () => {
    setInviteBusy(true);
    const targets = profiles.filter((p) => selected.has(p.uuid) && p.email);
    let ok = 0;
    for (const p of targets) {
      try {
        await sendInvitation(p.uuid, p.email ?? "", "bp", inviteUrls.bp);
        ok += 1;
      } catch {
        // keep going; failures are reported in the summary count
      }
    }
    setInviteBusy(false);
    setSelected(new Set());
    showSuccessToast(`${ok} von ${targets.length} Einladung(en) gesendet`);
  };

  const someSel = selected.size > 0;

  return (
    <div className={css.membersTab}>
      {/* Toolbar sits outside the list card (bp-frontend keeps the filter bar
          outside so its dropdowns aren't clipped by the card's overflow). */}
      <div className={css.membersToolbar}>
        <div className={css.listToolbarRow}>
          <div className={css.listToolbarLeft}>
            <div className={css.searchBox}>
              <SearchInput
                name="member-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Name oder E-Mail …"
              />
            </div>
            <div className={css.filterBox}>
              <Select
                name="role-filter"
                options={roleSelectOptions}
                value={roleSelectOptions.find((o) => o.value === roleFilter) ?? null}
                isSearchable={false}
                onChange={(opt: MultiValue<SelectOptionType> | SingleValue<SelectOptionType>) =>
                  setRoleFilter(String((opt as SelectOptionType).value))
                }
              />
            </div>
            <div className={css.filterBox}>
              <Select
                name="status-filter"
                options={STATUS_OPTIONS}
                value={STATUS_OPTIONS.find((o) => o.value === statusFilter) ?? null}
                isSearchable={false}
                onChange={(opt: MultiValue<SelectOptionType> | SingleValue<SelectOptionType>) =>
                  setStatusFilter(String((opt as SelectOptionType).value) as "all" | AccountStatus)
                }
              />
            </div>
          </div>
          <div className={css.listToolbarRight}>
            <span className={css.countMuted}>
              {filtered.length} von {profiles.length}
            </span>
            <Dropdown
              disabled={!someSel}
              trigger={
                <Button hierarchy="tertiary" height="s" disabled={!someSel}>
                  Aktion für Auswahl
                </Button>
              }
            >
              <DropdownMenu
                data={[
                  {
                    label: "Einladung senden",
                    disabled: !someSel || inviteBusy,
                    onClick: () => void bulkInvite(),
                  },
                  {
                    label: "Lizenz verwalten",
                    disabled: !someSel,
                    onClick: () => setBulkLicenseOpen(true),
                  },
                ]}
              />
            </Dropdown>
            <Dropdown
              trigger={
                <Button hierarchy="secondary" height="s" icon={<AddIcon />}>
                  Hinzufügen
                </Button>
              }
            >
              <DropdownMenu
                data={[
                  {
                    label: "Lehrkraft hinzufügen",
                    onClick: () => {
                      setAddRole("TEACHER");
                      setAdding(true);
                    },
                  },
                  {
                    label: "Schüler:in hinzufügen",
                    onClick: () => {
                      setAddRole("STUDENT");
                      setAdding(true);
                    },
                  },
                  {
                    label: "Eltern hinzufügen",
                    onClick: () => {
                      setAddRole("PARENT");
                      setAdding(true);
                    },
                  },
                  {
                    label: "Sonstige hinzufügen",
                    onClick: () => {
                      setAddRole("OTHER");
                      setAdding(true);
                    },
                  },
                ]}
              />
            </Dropdown>
          </div>
        </div>
      </div>

      {/* List card (bp-frontend: <BpCard noPadding> around the PersonsList) */}
      <Card contentPadding="none">
        {/* List head: select-all + selection count (bp-frontend PersonsList .actions) */}
        {profiles.length > 0 && (
          <div className={css.listHead}>
            <Checkbox
              name="select-all"
              label={allSelected ? "Auswahl aufheben" : "Alle auswählen"}
              checked={allSelected}
              onChange={toggleAll}
              className={css.selectAll}
            />
            <span className={css.listHeadInfo}>
              {selected.size} von {filtered.length} ausgewählt
            </span>
          </div>
        )}

        <MemberList
          isEmpty={filtered.length === 0}
          emptyTitle="Keine Mitglieder"
          emptySub="Passe Suche oder Filter an, oder füge ein Mitglied hinzu."
        >
          {filtered.map((p) => {
            const name = `${p.firstName ?? ""} ${p.lastName ?? ""}`.trim() || p.email || "—";
            const userUuid = p.user?.uuid;
            const roles = rolesForOrg(p, org.uuid);
            const ms = memberPresentation(memberStatus(p));
            const isSel = selected.has(p.uuid);
            return (
              <MemberRow
                key={p.uuid}
                selected={isSel}
                onClick={
                  userUuid ? () => navigate(`/users/${userUuid}/orgs/${org.uuid}`) : undefined
                }
                checkbox={
                  <Checkbox
                    name={`sel-${p.uuid}`}
                    checked={isSel}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => toggleSel(p.uuid)}
                  />
                }
                status={<UserStatusIcon status={ms.icon} size={28} />}
                statusTint={ms.tint}
                name={name}
                sub={p.email}
                labels={
                  roles.length > 0 || ms.badge ? (
                    <>
                      {roles.map((r) => (
                        <PersonLabel key={r} source="role" value={r} />
                      ))}
                      {ms.badge && <StatusBadge status={ms.badge.status} label={ms.badge.label} />}
                    </>
                  ) : undefined
                }
                actions={
                  <Dropdown
                    trigger={
                      <Button
                        hierarchy="tertiary"
                        icon={<DotsHorizontalIcon />}
                        ariaLabel="Aktionen"
                        onClick={(e) => e.stopPropagation()}
                      />
                    }
                  >
                    <DropdownMenu
                      data={[
                        ...(userUuid
                          ? [
                              {
                                label: "Benutzer öffnen",
                                onClick: () => navigate(`/users/${userUuid}/orgs/${org.uuid}`),
                              },
                            ]
                          : [
                              {
                                label: "Konto verknüpfen",
                                onClick: () => setLinking({ uuid: p.uuid, name }),
                              },
                            ]),
                        {
                          label: "Profil bearbeiten",
                          onClick: () =>
                            setEditing({
                              uuid: p.uuid,
                              firstName: p.firstName,
                              lastName: p.lastName,
                              email: p.email,
                              orgUuid: org.uuid,
                              role: rawRoleForOrg(p, org.uuid),
                            }),
                        },
                        ...(p.email
                          ? [
                              {
                                label: "Einladung senden",
                                onClick: () => void inviteOne(p.uuid, p.email ?? ""),
                              },
                            ]
                          : []),
                        {
                          label: "Lizenz verwalten",
                          onClick: () => setLicenseTarget({ uuid: p.uuid, name }),
                        },
                        { type: "ruler" },
                        {
                          label: "Profil löschen",
                          color: "error",
                          onClick: () => void onDelete(p.uuid, name),
                        },
                      ]}
                    />
                  </Dropdown>
                }
              />
            );
          })}
        </MemberList>
      </Card>

      {adding && (
        <AddMemberModal
          org={{ uuid: org.uuid, name: org.name }}
          invitationUrl={inviteUrls.bp}
          initialRole={addRole}
          onClose={() => setAdding(false)}
          onAdded={() => setAdding(false)}
        />
      )}
      {editing && (
        <EditProfileModal
          profile={editing}
          onClose={() => setEditing(null)}
          onSaved={() => setEditing(null)}
        />
      )}
      {linking && (
        <LinkAccountModal
          profile={{
            uuid: linking.uuid,
            name: linking.name,
            org: { uuid: org.uuid, name: org.name },
          }}
          onClose={() => setLinking(null)}
          onLinked={() => {
            setLinking(null);
            refetch({ requestPolicy: "network-only" });
          }}
        />
      )}
      {licenseTarget && (
        <LicenseModal
          org={{ uuid: org.uuid, name: org.name }}
          profile={licenseTarget}
          onClose={() => setLicenseTarget(null)}
          onSaved={() => setLicenseTarget(null)}
        />
      )}
      {bulkLicenseOpen && (
        <BulkLicenseModal
          org={{ uuid: org.uuid, name: org.name }}
          profileUuids={[...selected]}
          onClose={() => setBulkLicenseOpen(false)}
          onSaved={() => {
            setBulkLicenseOpen(false);
            setSelected(new Set());
          }}
        />
      )}
      <ConfirmationDialog />
    </div>
  );
}
